import { useCallback, useMemo, useState, type ReactNode, type RefObject } from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import {
  getAssistantSurfaceLayoutKey,
  useAssistantSurfaceMainContentBoundary,
  useAssistantSurfaceRailOffset,
} from 'tripPlanner/assistant/layout/AssistantSurfaceLayoutContext'
import {
  OFFER_PAGE_ASSISTANT_COLLAPSED_INPUT_BOTTOM_OFFSET,
  OFFER_PAGE_ASSISTANT_MIN_SCREEN_SIZE,
  OFFER_PAGE_ASSISTANT_SIDEBAR_WIDTH,
} from './constants'
import { OfferPageAssistantLayoutProvider } from './OfferPageAssistantLayoutContext'

const OFFER_PAGE_ASSISTANT_RAIL_LAYOUT_KEY = getAssistantSurfaceLayoutKey('accommodation_offer_page')

const OfferPageBody = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 0;

  ${mediaQueryUp[OFFER_PAGE_ASSISTANT_MIN_SCREEN_SIZE]} {
    &.with-assistant-sidebar {
      flex-direction: row;
      align-items: stretch;
    }
  }
`

const MainContent = styled(Group)`
  flex-grow: 1;
  min-width: 0;
`

const AssistantLayoutColumn = styled.div`
  flex: 0 0 ${rem(OFFER_PAGE_ASSISTANT_SIDEBAR_WIDTH)};
  width: ${rem(OFFER_PAGE_ASSISTANT_SIDEBAR_WIDTH)};
  min-width: 0;
`

const AssistantLayoutPortal = styled.div`
  height: 100%;
`

const AssistantCollapsedLayoutPortal = styled.div`
  position: sticky;
  top: calc((var(--vh, 1vh) * 100) - ${rem(OFFER_PAGE_ASSISTANT_COLLAPSED_INPUT_BOTTOM_OFFSET)});
  z-index: 30;
  flex: 0 0 0;
  align-self: flex-start;
  width: 0;
  height: 0;
`

interface Props {
  children: ReactNode
  headlessMode: boolean
  mainContentRef?: RefObject<HTMLDivElement | null>
}

export default function OfferPageAssistantRailLayout({
  children,
  headlessMode,
  mainContentRef,
}: Props) {
  const [assistantPortalElement, setAssistantPortalElement] = useState<HTMLDivElement | null>(null)
  const [collapsedPortalElement, setCollapsedPortalElement] = useState<HTMLDivElement | null>(null)
  const [isAssistantAvailable, setIsAssistantAvailable] = useState(false)
  const isAssistantViewportEligible = useScreenSizeUp(OFFER_PAGE_ASSISTANT_MIN_SCREEN_SIZE)
  const isAssistantLayoutEligible = isAssistantViewportEligible && !headlessMode
  const isAssistantRailActive = isAssistantAvailable && isAssistantLayoutEligible
  const assistantRailOffsetPx = isAssistantRailActive ? OFFER_PAGE_ASSISTANT_SIDEBAR_WIDTH : 0
  const handleAssistantAvailabilityChange = useCallback((available: boolean) => {
    setIsAssistantAvailable(available && isAssistantLayoutEligible)
  }, [isAssistantLayoutEligible])
  const assistantLayoutContext = useMemo(() => ({
    collapsedPortalElement: isAssistantLayoutEligible ? collapsedPortalElement ?? undefined : undefined,
    isLayoutEligible: isAssistantLayoutEligible,
    portalElement: isAssistantRailActive ? assistantPortalElement ?? undefined : undefined,
    onAvailabilityChange: handleAssistantAvailabilityChange,
  }), [
    assistantPortalElement,
    collapsedPortalElement,
    handleAssistantAvailabilityChange,
    isAssistantLayoutEligible,
    isAssistantRailActive,
  ])
  const mainContentBoundaryRef = useAssistantSurfaceMainContentBoundary(
    OFFER_PAGE_ASSISTANT_RAIL_LAYOUT_KEY,
    mainContentRef,
  )

  useAssistantSurfaceRailOffset(OFFER_PAGE_ASSISTANT_RAIL_LAYOUT_KEY, assistantRailOffsetPx)

  return (
    <OfferPageAssistantLayoutProvider value={assistantLayoutContext}>
      <OfferPageBody className={clsx({ 'with-assistant-sidebar': isAssistantRailActive })}>
        {isAssistantLayoutEligible && (
          <AssistantCollapsedLayoutPortal data-testid="offer-page-assistant-collapsed-portal" ref={setCollapsedPortalElement} />
        )}
        {isAssistantRailActive && (
          <AssistantLayoutColumn>
            <AssistantLayoutPortal ref={setAssistantPortalElement} />
          </AssistantLayoutColumn>
        )}
        <MainContent
          data-boundary-id={OFFER_PAGE_ASSISTANT_RAIL_LAYOUT_KEY}
          ref={mainContentBoundaryRef}
          direction="vertical"
          verticalAlign="space-between"
        >
          {children}
        </MainContent>
      </OfferPageBody>
    </OfferPageAssistantLayoutProvider>
  )
}
